import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import MetricsGrid from '../components/financials/MetricsGrid';
import TransactionsList from '../components/financials/TransactionsList';
import RevenueChart from '../components/financials/RevenueChart';
import MonthlyExpenseManager from '../components/financials/MonthlyExpenseManager';
import PnLTable from '../components/financials/PnLTable';
import MarginCard from '../components/financials/MarginCard';
import MarginChart from '../components/financials/MarginChart';
import ManualTransactionModal from '../components/financials/ManualTransactionModal';
import { useStripeFinancials } from '../context/StripeFinancialsContext';
import { useUser } from '../context/UserContext';
import { Button, Tabs } from '../components/ui';

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

type TabKey = 'overview' | 'expenses' | 'pnl' | 'transactions';

const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Overview',
  expenses: 'Expenses',
  pnl: 'P&L',
  transactions: 'Transactions',
};

const TAB_KEYS: Record<string, TabKey> = {
  Overview: 'overview',
  Expenses: 'expenses',
  'P&L': 'pnl',
  Transactions: 'transactions',
};

export default function Financials() {
  const { syncStripeData, isLoading, error, stripeConfig, refreshTransactions } = useStripeFinancials();
  const { user, isUserLoaded } = useUser();
  const [showManualModal, setShowManualModal] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const tabStripRef = useRef<HTMLDivElement>(null);

  // Narrow phones scroll the tab strip; keep the selected tab on screen
  useEffect(() => {
    const strip = tabStripRef.current;
    const btn = [...(strip?.querySelectorAll('button') ?? [])].find((b) => b.textContent === TAB_LABELS[activeTab]);
    if (!strip || !btn || strip.scrollWidth <= strip.clientWidth) return;
    strip.scrollTo({ left: btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2, behavior: 'smooth' });
  }, [activeTab]);

  // Only admin users can access financials
  if (!isUserLoaded) {
    return null;
  }

  const userRole = normalizeRole(user?.role);

  if (userRole !== 'admin') {
    return <Navigate to="/clients" replace />;
  }

  const handleSync = async () => {
    await syncStripeData();
  };

  const handleManualTransactionSuccess = () => {
    refreshTransactions();
  };

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Financials"
          actions={
            <span style={{ display: 'inline-flex', gap: '8px', marginLeft: '6px' }}>
              <Button size="sm" icon="plus" onClick={() => setShowManualModal(true)}>
                Add Transaction
              </Button>
              <Button
                size="sm"
                variant="secondary"
                icon="refresh-cw"
                className="ck-btn hover-lift pressable hover-spin"
                onClick={handleSync}
                disabled={isLoading}
              >
                {isLoading ? 'Syncing...' : 'Sync'}
              </Button>
            </span>
          }
        />

        {error && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* Tabs + last synced */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-6)',
          }}
        >
          {/* Scrolls on its own on narrow phones rather than pushing the page wider */}
          <div ref={tabStripRef} className="overflow-x-auto scrollbar-hidden" style={{ flex: 1, minWidth: 0 }}>
            <Tabs
              tabs={Object.keys(TAB_KEYS)}
              active={TAB_LABELS[activeTab]}
              onChange={(tab) => setActiveTab(TAB_KEYS[tab])}
              style={{ width: 'max-content', minWidth: '100%' }}
            />
          </div>
          {stripeConfig?.last_synced_at && (
            <span
              className="t-num hidden md:inline"
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-faint)',
                paddingBottom: '10px',
                whiteSpace: 'nowrap',
              }}
            >
              Last synced:{' '}
              {new Date(stripeConfig.last_synced_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
              ,{' '}
              {new Date(stripeConfig.last_synced_at).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <MetricsGrid />

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr]" style={{ gap: 'var(--space-4)' }}>
              <MarginCard />
              <RevenueChart />
            </div>
          </div>
        )}

        {activeTab === 'expenses' && <MonthlyExpenseManager />}

        {activeTab === 'pnl' && (
          <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <MarginChart />
            <PnLTable />
          </div>
        )}

        {activeTab === 'transactions' && <TransactionsList />}
      </main>

      <ManualTransactionModal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        onSuccess={handleManualTransactionSuccess}
      />
    </div>
  );
}
